import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Edit2, Save, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Formattage sécurisé de YYYY-MM-DD en évitant tout décalage de fuseau horaire (ex: UTC-10 Tahiti)
const formatSafeDate = (dateStr) => {
  if (!dateStr) return '-';
  const parts = String(dateStr).split('-');
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    const localDate = new Date(y, m, d);
    return `${format(localDate, 'dd/MM/yyyy')} (${format(localDate, 'EEE d MMM yyyy', { locale: fr })})`;
  }
  return dateStr;
};

const ReservationDetailsModal = ({ reservation, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [excursions, setExcursions] = useState([]);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (reservation) {
      const notesStr = reservation.notes || '';
      
      // Récupérer la nationalité (depuis les notes ou colonne dédiée)
      const natMatch = notesStr.match(/Nationalit[ée]\s*:\s*(.+?)(?:\n|$)/i);
      const parsedNat = reservation.nationality || (natMatch ? natMatch[1].trim() : '');

      // Récupérer l'âge (simple ou plusieurs comme "25, 27")
      const ageMatch = notesStr.match(/Ages?\s*:\s*(.+?)(?:\n|$)/i);
      const parsedAge = (reservation.age !== null && reservation.age !== undefined && reservation.age !== '') 
        ? String(reservation.age) 
        : (ageMatch ? ageMatch[1].trim() : '');

      // Récupérer l'hébergement / provenance
      const provMatch = notesStr.match(/H[ée]bergement\s*(?:\/\s*Provenance)?\s*:\s*(.+?)(?:\n|$)/i);
      const parsedProv = provMatch ? provMatch[1].trim() : '';

      // Récupérer le créneau depuis la colonne dédiée, sinon parser les notes
      let parsedSlot = reservation.time_slot || null;
      if (!parsedSlot) {
        const slotMatch = notesStr.match(/Cr[ée]neau\s*:\s*(matin|apresmidi|journee)/i);
        parsedSlot = slotMatch ? slotMatch[1].toLowerCase() : 'journee';
      }

      setFormData({
        first_name: reservation.first_name || '',
        client_name: reservation.client_name || '',
        age: parsedAge,
        nationality: parsedNat,
        accommodation: parsedProv,
        client_email: reservation.client_email || '',
        client_phone: reservation.client_phone || '',
        number_of_people: reservation.number_of_people || 1,
        monoplace_qty: reservation.monoplace_qty || 0,
        biplace_qty: reservation.biplace_qty || 0,
        time_slot: parsedSlot,
        reservation_date: reservation.reservation_date || '',
        source: reservation.source || 'Site Web',
        total_price: reservation.total_price !== null && reservation.total_price !== undefined ? reservation.total_price : '',
        notes: reservation.notes || '',
        admin_notes: reservation.admin_notes || '',
        message: reservation.message || ''
      });
    }
  }, [reservation]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const selectedExcursion = excursions.find(ex => ex.id === formData.excursion_id);

      let finalNotes = formData.notes || '';
      if (formData.nationality) {
        if (finalNotes.match(/Nationalit[ée]:/i)) {
           finalNotes = finalNotes.replace(/Nationalit[ée]:\s*(.+?)(?:\n|$)/i, `Nationalité: ${formData.nationality}\n`);
        } else {
           finalNotes = `Nationalité: ${formData.nationality}\n` + finalNotes;
        }
      }
      if (formData.age) {
        if (finalNotes.match(/Ages?:/i)) {
           finalNotes = finalNotes.replace(/Ages?:\s*(.+?)(?:\n|$)/i, `Ages: ${formData.age}\n`);
        } else {
           finalNotes += `\nAges: ${formData.age}`;
        }
      }
      if (formData.accommodation) {
        if (finalNotes.match(/H[ée]bergement\s*(?:\/\s*Provenance)?:/i)) {
           finalNotes = finalNotes.replace(/H[ée]bergement\s*(?:\/\s*Provenance)?:\s*(.+?)(?:\n|$)/i, `Hébergement / Provenance: ${formData.accommodation}\n`);
        } else {
           finalNotes += `\nHébergement / Provenance: ${formData.accommodation}`;
        }
      }

      const updates = {
        first_name: formData.first_name,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        number_of_people: parseInt(formData.number_of_people, 10),
        monoplace_qty: parseInt(formData.monoplace_qty, 10) || 0,
        biplace_qty: parseInt(formData.biplace_qty, 10) || 0,
        time_slot: formData.time_slot,
        reservation_date: formData.reservation_date,
        source: formData.source,
        total_price: formData.total_price ? parseInt(formData.total_price, 10) : null,
        notes: finalNotes
      };

      const { error } = await supabase
        .from('reservations')
        .update(updates)
        .eq('id', reservation.id);

      if (error) throw error;

      toast.success('Détails mis à jour avec succès');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Voulez-vous vraiment supprimer cette réservation ?")) {
      setIsSaving(true);
      try {
        const { error } = await supabase
          .from('reservations')
          .update({ status: 'deleted' })
          .eq('id', reservation.id);

        if (error) throw error;

        toast.success('Réservation supprimée');
        setIsEditing(false);
        onClose();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error(err);
        toast.error('Erreur lors de la suppression');
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!reservation || !formData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        setIsEditing(false);
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-2xl bg-white text-gray-900 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-center pr-8">
            <div>
              <DialogTitle className="text-xl font-bold">
                Détails de la réservation
              </DialogTitle>
              {formData?.reservation_date && (
                <p className="text-xs text-cyan-700 font-semibold mt-0.5">
                  Prévue le {formatSafeDate(formData.reservation_date)}
                </p>
              )}
            </div>
            {!isEditing ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Modifier
                </Button>
                <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setIsEditing(false); setFormData({...reservation}); }}>
                  <X className="w-4 h-4 mr-2" /> Annuler
                </Button>
                <Button variant="default" size="sm" onClick={handleSave} disabled={isSaving} className="bg-primary text-white">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Prénom</Label>
              {isEditing ? (
                <Input name="first_name" value={formData.first_name} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.first_name || '-'}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Nom</Label>
              {isEditing ? (
                <Input name="client_name" value={formData.client_name} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.client_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Âge</Label>
              {isEditing ? (
                <Input type="text" name="age" value={formData.age} onChange={handleChange} placeholder="Ex: 25, 27" className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.age || '-'}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Nationalité</Label>
              {isEditing ? (
                <select 
                  name="nationality" 
                  value={formData.nationality} 
                  onChange={handleChange} 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Sélectionner</option>
                  {["Française", "Américaine", "Canadienne", "Italienne", "Allemande", "Espagnole", "Polynésienne", "Autre"].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                  {formData.nationality && !["Française", "Américaine", "Canadienne", "Italienne", "Allemande", "Espagnole", "Polynésienne", "Autre"].includes(formData.nationality) && (
                    <option value={formData.nationality}>{formData.nationality}</option>
                  )}
                </select>
              ) : (
                <p className="font-medium">{formData.nationality || '-'}</p>
              )}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Email</Label>
              {isEditing ? (
                <Input type="email" name="client_email" value={formData.client_email} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.client_email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Téléphone</Label>
              {isEditing ? (
                <Input name="client_phone" value={formData.client_phone} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.client_phone || '-'}</p>
              )}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Excursion details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Date</Label>
              {isEditing ? (
                <Input type="date" name="reservation_date" value={formData.reservation_date} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-semibold text-gray-900">{formatSafeDate(formData.reservation_date)}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Participants</Label>
              {isEditing ? (
                <Input type="number" name="number_of_people" value={formData.number_of_people} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.number_of_people}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Monoplaces</Label>
              {isEditing ? (
                <Input type="number" min="0" name="monoplace_qty" value={formData.monoplace_qty} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.monoplace_qty}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Biplaces</Label>
              {isEditing ? (
                <Input type="number" min="0" name="biplace_qty" value={formData.biplace_qty} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.biplace_qty}</p>
              )}
            </div>
            {/* Créneau — champ dédié */}
            <div className="space-y-2 col-span-2">
              <Label className="text-cyan-700 font-bold">Créneau</Label>
              {isEditing ? (
                <select
                  name="time_slot"
                  value={formData.time_slot}
                  onChange={handleChange}
                  className="flex h-10 w-full rounded-md border-2 border-cyan-400 bg-cyan-50 px-3 py-2 text-sm text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="matin">Matin (08h00 – 11h30)</option>
                  <option value="apresmidi">Après-midi (13h30 – 17h00)</option>
                  <option value="journee">Journée complète (08h00 – 17h00)</option>
                </select>
              ) : (
                <p className="font-semibold text-cyan-700">
                  {formData.time_slot === 'matin' ? '🌅 Matin (08h00 – 11h30)'
                   : formData.time_slot === 'apresmidi' ? '🌞 Après-midi (13h30 – 17h00)'
                   : '☀️ Journée complète (08h00 – 17h00)'}
                </p>
              )}
            </div>

            {/* Provenance / Hébergement */}
            <div className="space-y-2 col-span-2">
              <Label className="text-gray-500 font-semibold">📍 Provenance / Hébergement</Label>
              {isEditing ? (
                <Input 
                  name="accommodation" 
                  value={formData.accommodation || ''} 
                  onChange={handleChange} 
                  placeholder="Hôtel, pension, paquebot..." 
                  className="text-gray-900" 
                />
              ) : (
                <p className={`font-semibold p-2.5 rounded-lg border text-sm ${
                  (formData.accommodation || '').toLowerCase().includes('paquebot') || (formData.accommodation || '').toLowerCase().includes('croisi')
                    ? 'bg-amber-50 text-amber-900 border-amber-200' 
                    : 'bg-cyan-50 text-cyan-900 border-cyan-100'
                }`}>
                  {formData.accommodation || '-'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Source</Label>
              {isEditing ? (
                <select 
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Site Web">Site Web</option>
                  <option value="Airbnb">Airbnb</option>
                  <option value="Getyourguide">Getyourguide</option>
                  <option value="Manawa">Manawa</option>
                  <option value="Viator">Viator</option>
                  <option value="Direct">Direct</option>
                </select>
              ) : (
                <p className="font-medium">{formData.source}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Tarif (XPF)</Label>
              {isEditing ? (
                <Input type="number" min="0" name="total_price" value={formData.total_price} onChange={handleChange} className="text-gray-900" />
              ) : (
                <p className="font-medium">{formData.total_price !== null && formData.total_price !== undefined && formData.total_price !== '' ? `${formData.total_price} XPF` : '-'}</p>
              )}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Notes */}
          <div className="space-y-4">
            {(!isEditing && formData.message) && (
              <div className="space-y-2">
                <Label className="text-gray-500">Message original (Client)</Label>
                <div className="bg-gray-50 p-3 rounded-md italic text-gray-700 text-sm">
                  {formData.message}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-500">Notes / Équipement</Label>
              {isEditing ? (
                <Textarea name="notes" value={formData.notes} onChange={handleChange} className="text-gray-900" rows={4} />
              ) : (
                <div className="bg-gray-50 p-3 rounded-md text-gray-700 text-sm min-h-[60px] whitespace-pre-line leading-relaxed">
                  {formData.notes || "Aucune note."}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Notes Internes (Admin)</Label>
              {isEditing ? (
                <Textarea name="admin_notes" value={formData.admin_notes} onChange={handleChange} className="text-gray-900" rows={3} />
              ) : (
                <div className="bg-gray-50 p-3 rounded-md text-gray-700 text-sm min-h-[60px]">
                  {formData.admin_notes || "Aucune note interne."}
                </div>
              )}
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDetailsModal;